import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function TauntOverlay({ lastPrank }) {
  const [activePranks, setActivePranks] = useState([]);

  useEffect(() => {
    if (lastPrank) {
      const id = Date.now();
      setActivePranks(prev => [...prev, { ...lastPrank, id }]);
    }
  }, [lastPrank]);

  const removePrank = (id) => {
    setActivePranks(prev => prev.filter(p => p.id !== id));
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      pointerEvents: 'none',
      zIndex: 9999,
      overflow: 'hidden'
    }}>
      <AnimatePresence>
        {activePranks.map(prank => (
          <PrankAnimation 
            key={prank.id} 
            prank={prank} 
            onComplete={() => removePrank(prank.id)} 
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

function PrankAnimation({ prank, onComplete }) {
  const [coords, setCoords] = useState(null);
  const isEmoji = prank.item.startsWith('emoji:');
  const emojiType = isEmoji ? prank.item.split(':')[1] : null;

  useEffect(() => {
    const findCoords = () => {
      const targetEl = document.querySelector(`[data-player-id="${prank.target_id}"]`);

      if (targetEl) {
        const tRect = targetEl.getBoundingClientRect();
        setCoords({
          center: { x: tRect.left + tRect.width / 2, y: tRect.top + tRect.height / 2 }
        });
      } else {
        onComplete();
      }
    };

    const timer = setTimeout(findCoords, 50);
    return () => clearTimeout(timer);
  }, [prank, onComplete]);

  // EMOJI ANIMATION (Moving towards center)
  if (isEmoji) {
    if (!coords) return null;

    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    
    // Move 40% towards the center of the screen
    const targetX = coords.center.x + (centerX - coords.center.x) * 0.4;
    const targetY = coords.center.y + (centerY - coords.center.y) * 0.4;

    return (
      <motion.img
        src={`/assets/emojis/${emojiType}.webp`}
        initial={{ 
          x: coords.center.x - 50, 
          y: coords.center.y - 50, 
          scale: 0.8, 
          opacity: 0 
        }}
        animate={{ 
          x: targetX - 50,
          y: targetY - 50, 
          scale: 1.5, 
          opacity: [0, 1, 1, 0] 
        }}
        transition={{ 
          duration: 2.5, 
          times: [0, 0.2, 0.8, 1],
          ease: "easeOut" 
        }}
        style={{
          position: 'absolute',
          width: '100px',
          height: '100px',
          zIndex: 10002,
          pointerEvents: 'none'
        }}
        onAnimationComplete={onComplete}
      />
    );
  }

  return (
    <PrankMovement prank={prank} onComplete={onComplete} />
  );
}

function PrankMovement({ prank, onComplete }) {
  const [phase, setPhase] = useState('flying');
  const [movementCoords, setMovementCoords] = useState(null);

  useEffect(() => {
    const findSource = () => {
      const el = document.querySelector(`[data-player-id="${prank.from_id}"]`);
      const targetEl = document.querySelector(`[data-player-id="${prank.target_id}"]`);
      if (el && targetEl) {
        const sRect = el.getBoundingClientRect();
        const tRect = targetEl.getBoundingClientRect();
        setMovementCoords({
          start: { x: sRect.left + sRect.width / 2, y: sRect.top + sRect.height / 2 },
          end: { x: tRect.left + tRect.width / 2, y: tRect.top + tRect.height / 2 }
        });
      } else {
        onComplete();
      }
    };
    findSource();
  }, [prank, onComplete]);

  if (!movementCoords) return null;

  return (
    <>
      {phase === 'flying' && (
        <motion.img
          src={`/assets/taunts/${prank.item}_fly.png`}
          initial={{ 
            x: movementCoords.start.x - 40, 
            y: movementCoords.start.y - 40, 
            scale: 0.2, 
            rotate: 0,
            opacity: 1
          }}
          animate={{ 
            x: movementCoords.end.x - 40, 
            y: movementCoords.end.y - 40, 
            scale: 0.8, 
            rotate: 1080 
          }}
          transition={{ 
            duration: 0.7, 
            ease: [0.4, 0, 0.2, 1]
          }}
          style={{
            position: 'absolute',
            width: '80px',
            height: '80px',
            zIndex: 10000,
            pointerEvents: 'none'
          }}
          onAnimationComplete={() => {
            setPhase('hit');
            const targetEl = document.querySelector(`[data-player-id="${prank.target_id}"]`);
            if (targetEl) {
              targetEl.animate([
                { transform: 'scale(1) translate(0, 0)', zIndex: 50 },
                { transform: 'scale(1.3) translate(0, 0)', zIndex: 100, offset: 0.15 },
                { transform: 'scale(1.1) translate(-10px, -10px)', zIndex: 100, offset: 0.3 },
                { transform: 'scale(1.1) translate(10px, 10px)', offset: 0.5 },
                { transform: 'scale(1.1) translate(-10px, 10px)', offset: 0.7 },
                { transform: 'scale(1.1) translate(10px, -10px)', offset: 0.85 },
                { transform: 'scale(1) translate(0, 0)', zIndex: 50 }
              ], { duration: 500, easing: 'ease-out' });
            }
          }}
        />
      )}

      {phase === 'hit' && (
        <motion.img
          src={`/assets/taunts/${prank.item}_hit.png`}
          initial={{ 
            x: movementCoords.end.x - 75, 
            y: movementCoords.end.y - 75, 
            scale: 0.5, 
            opacity: 0,
            rotate: 0 
          }}
          animate={{ 
            scale: [1.2, 0.7], 
            opacity: 1 
          }}
          exit={{ opacity: 0, scale: 0.5 }}
          transition={{ 
            scale: { duration: 0.6, times: [0, 1], ease: "easeOut" },
            opacity: { duration: 0.2 }
          }}
          style={{
            position: 'absolute',
            width: '150px',
            height: '150px',
            zIndex: 10001,
            pointerEvents: 'none'
          }}
          onAnimationComplete={() => {
            setTimeout(onComplete, 2500); 
          }}
        />
      )}
    </>
  );
}
